import { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider, CssBaseline, Box, CircularProgress, Snackbar, Alert } from '@mui/material';
import { theme } from './theme';
import { AppRoutes } from './routes';
import { useAuthStore } from './stores/authStore';
import { subscriptionService } from './services/subscriptionService';
import api from './services/api';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  const setCurrentVet = useAuthStore((state) => state.setCurrentVet);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setSubscription = useAuthStore((state) => state.setSubscription);
  const user = useAuthStore((state) => state.user);
  const setAuth = useAuthStore((state) => state.setAuth);
  const [usageLimitMsg, setUsageLimitMsg] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => setUsageLimitMsg((e as CustomEvent<string>).detail);
    window.addEventListener('vetai:usage-limit', handler);
    return () => window.removeEventListener('vetai:usage-limit', handler);
  }, []);

  // On startup, if we have a token but no user (e.g. first login before localStorage was persisted),
  // re-fetch the user profile so role/admin access works after reload.
  useEffect(() => {
    if (isAuthenticated && !user) {
      api.get('/users/me').then((res) => {
        const accessToken = localStorage.getItem('access_token') || '';
        const refreshToken = localStorage.getItem('refresh_token') || '';
        setAuth(res.data, accessToken, refreshToken);
      }).catch(() => {});
    }
  }, [isAuthenticated, user, setAuth]);

  // Load subscription status once authenticated
  useEffect(() => {
    if (isAuthenticated) {
      subscriptionService.getMySubscription()
        .then(setSubscription)
        .catch(() => {}); // Silently ignore — user may not have a token yet
    }
  }, [isAuthenticated, setSubscription]);

  // In the extension, localStorage may be partitioned and not survive panel close/reopen.
  // Wait briefly for the extension to inject stored tokens before rendering routes.
  const [isInitializing, setIsInitializing] = useState(() => {
    const isAlreadyAuth = !!localStorage.getItem('current_vet_id');
    const isInExtension = window !== window.parent;
    return !isAlreadyAuth && isInExtension;
  });

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('[DEBUG App] message received:', event.origin, event.data?.type, event.data);
      if (event.data?.type === 'VETAI_INJECT_TOKENS') {
        const { access_token, refresh_token, current_vet_id, current_vet_name } = event.data.payload ?? {};
        console.log('[DEBUG App] VETAI_INJECT_TOKENS payload:', {
          has_access_token: !!access_token,
          has_refresh_token: !!refresh_token,
          current_vet_id: current_vet_id || null,
          current_vet_name: current_vet_name || null,
        });
        if (access_token) {
          localStorage.setItem('access_token', access_token);
          if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
          // FIX: also update Zustand store so route guard sees the token immediately
          useAuthStore.setState({
            accessToken: access_token,
            refreshToken: refresh_token ?? null,
            isAuthenticated: true,
          });
          console.log('[DEBUG App] access_token written to localStorage + Zustand store');
        } else {
          console.warn('[DEBUG App] VETAI_INJECT_TOKENS received but no access_token in payload');
        }
        if (current_vet_id) {
          localStorage.setItem('current_vet_id', current_vet_id);
          localStorage.setItem('current_vet_name', current_vet_name ?? '');
          useAuthStore.getState().setCurrentVet(current_vet_id, current_vet_name ?? '');
          console.log('[DEBUG App] setCurrentVet called with', current_vet_id);
        } else {
          console.warn('[DEBUG App] VETAI_INJECT_TOKENS: no current_vet_id — store not updated');
        }
        setIsInitializing(false);
      }
    };

    window.addEventListener('message', handleMessage);
    console.log('[DEBUG App] message listener registered, isInitializing=', isInitializing, 'isInExtension=', window !== window.parent);

    // FIX: signal to sidepanel that our listener is ready — sidepanel will inject tokens
    // in response to this signal, eliminating the race condition where postMessage fires
    // before the listener is registered.
    if (window !== window.parent) {
      console.log('[DEBUG App] sending VETAI_APP_READY to parent');
      window.parent.postMessage({ type: 'VETAI_APP_READY' }, '*');
    }

    // Fallback: if no tokens arrive within 800ms, stop waiting (user is not logged in)
    let timeout: ReturnType<typeof setTimeout> | null = null;
    if (isInitializing) {
      timeout = setTimeout(() => setIsInitializing(false), 800);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      if (timeout) clearTimeout(timeout);
    };
  }, [setCurrentVet]);

  if (isInitializing) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Snackbar
          open={!!usageLimitMsg}
          autoHideDuration={6000}
          onClose={() => setUsageLimitMsg(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="warning" onClose={() => setUsageLimitMsg(null)}>
            {usageLimitMsg}
          </Alert>
        </Snackbar>
        <ReactQueryDevtools initialIsOpen={false} />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
